// @ts-nocheck
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import notify from '../utils/toast';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import ActionButtons from '../components/ActionButtons';
import AppModal from '../components/AppModal';

export default function Accounts() {
    const { t } = useI18n();
    const { hasRole, can } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [projects, setProjects] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('expenses');
    const [showExp, setShowExp] = useState(false);
    const [form, setForm] = useState({
        title: '', amount: '', expense_date: new Date().toISOString().slice(0, 10),
        category: 'material', project_id: '', account_id: '', payment_method: 'cash', notes: '',
    });
    const closeExpModal = () => {
        setShowExp(false);
        setForm({
            title: '', amount: '', expense_date: new Date().toISOString().slice(0, 10),
            category: 'material', project_id: '', account_id: '', payment_method: 'cash', notes: '',
        });
    };

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get('/accounts/chart'),
            api.get('/accounts/expenses'),
            api.get('/accounts/summary'),
            api.get('/projects').catch(() => ({ data: { data: [] } })),
        ]).then(([a, e, s, p]) => {
            setAccounts(a.data.data);
            setExpenses(e.data.data);
            setSummary(s.data.data);
            setProjects(p.data.data || []);
        }).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const createExpense = async (e) => {
        e.preventDefault();
        try {
            await api.post('/accounts/expenses', {
                ...form,
                amount: Number(form.amount),
                project_id: form.project_id || null,
                account_id: form.account_id || null,
            });
            closeExpModal();
            load();
        } catch (err) {
            notify.apiError(err);
        }
    };

    const approve = async (id) => {
        if (!window.confirm('Approve?')) return;
        try {
            await api.post(`/accounts/expenses/${id}/approve`);
            load();
        } catch (err) {
            notify.apiError(err);
        }
    };

    const reject = async (id) => {
        if (!window.confirm('Reject?')) return;
        try {
            await api.post(`/accounts/expenses/${id}/reject`);
            load();
        } catch (err) {
            notify.apiError(err);
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="mb-1">{t('accountsTitle')}</h4>
                    <p className="text-muted mb-0">{t('accountsDesc')}</p>
                </div>
                {can('accounts.create') && (
                    <button className="btn btn-primary btn-sm" onClick={() => setShowExp(true)}>
                        <i className="bi bi-plus-lg me-1"></i>{t('addExpense')}
                    </button>
                )}
            </div>

            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="card stat-card"><div className="card-body">
                        <div className="text-muted small">{t('totalExpense')}</div>
                        <div className="fs-5 fw-bold">৳ {summary?.totalExpense?.toLocaleString() ?? '—'}</div>
                    </div></div>
                </div>
                <div className="col-md-4">
                    <div className="card stat-card"><div className="card-body">
                        <div className="text-muted small">{t('pendingApproval')}</div>
                        <div className="fs-4 fw-bold text-warning">{summary?.pending ?? '—'}</div>
                    </div></div>
                </div>
                <div className="col-md-4">
                    <div className="card stat-card"><div className="card-body">
                        <div className="text-muted small">{t('chartOfAccounts')}</div>
                        <div className="fs-4 fw-bold">{summary?.accountCount ?? '—'}</div>
                    </div></div>
                </div>
            </div>

            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button className={`nav-link ${tab === 'expenses' ? 'active' : ''}`} onClick={() => setTab('expenses')}>{t('expenses')}</button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${tab === 'chart' ? 'active' : ''}`} onClick={() => setTab('chart')}>{t('chartOfAccounts')}</button>
                </li>
            </ul>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-success" /></div>
            ) : tab === 'expenses' ? (
                <div className="card table-card">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>No</th>
                                    <th>{t('title')}</th>
                                    <th>{t('project')}</th>
                                    <th>{t('category')}</th>
                                    <th>{t('amount')}</th>
                                    <th>{t('date')}</th>
                                    <th>{t('status')}</th>
                                    <th>{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.length === 0 ? (
                                    <tr><td colSpan={8} className="text-center text-muted py-4">{t('noData')}</td></tr>
                                ) : expenses.map((ex) => (
                                    <tr key={ex.id}>
                                        <td className="fw-semibold">{ex.expense_no}</td>
                                        <td>{ex.title}</td>
                                        <td>{ex.project?.name || '—'}</td>
                                        <td className="text-capitalize">{ex.category}</td>
                                        <td>৳ {Number(ex.amount).toLocaleString()}</td>
                                        <td>{ex.expense_date}</td>
                                        <td>
                                            <span className={`badge bg-${ex.status === 'approved' ? 'success' : ex.status === 'rejected' ? 'danger' : 'warning'} badge-status`}>
                                                {ex.status}
                                            </span>
                                        </td>
                                        <td>
                                            {ex.status === 'pending' && (
                                                <ActionButtons
                                                    canApprove={can('accounts.approve')}
                                                    onApprove={() => approve(ex.id)}
                                                    onReject={() => reject(ex.id)}
                                                    approveTitle="Approve voucher/expense"
                                                    rejectTitle="Reject"
                                                />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="card table-card">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>{t('accountCode')}</th>
                                    <th>{t('name')}</th>
                                    <th>{t('accountType')}</th>
                                    <th>{t('balance')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.map((a) => (
                                    <tr key={a.id}>
                                        <td className="fw-semibold">{a.code}</td>
                                        <td>{a.name}</td>
                                        <td className="text-capitalize">{a.type}</td>
                                        <td>৳ {Number(a.balance).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showExp && (
                <div className="modal show d-block app-modal" onClick={closeExpModal} style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <form className="modal-content" onSubmit={createExpense}>
                            <div className="modal-header">
                                <h5 className="modal-title">{t('addExpense')}</h5>
                                <button type="button" className="btn-close" onClick={closeExpModal} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-2">
                                    <label className="form-label">{t('title')} *</label>
                                    <input className="form-control" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                                </div>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label className="form-label">{t('amount')} *</label>
                                        <input type="number" className="form-control" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">{t('date')} *</label>
                                        <input type="date" className="form-control" required value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
                                    </div>
                                </div>
                                <div className="mb-2 mt-2">
                                    <label className="form-label">{t('category')}</label>
                                    <select className="form-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                        {['material','labor','transport','equipment','utility','office','marketing','legal','tax','other'].map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('project')}</label>
                                    <select className="form-select" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                                        <option value="">—</option>
                                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Account</label>
                                    <select className="form-select" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
                                        <option value="">—</option>
                                        {accounts.filter((a) => a.type === 'expense').map((a) => (
                                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('paymentMethod')}</label>
                                    <select className="form-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                                        <option value="cash">Cash</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="mobile_banking">Mobile Banking</option>
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeExpModal}>{t('cancel')}</button>
                                <button type="submit" className="btn btn-primary">{t('save')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
